import React from 'react';
import { ResumeData } from './resumeTypes';
import { AppliedTemplate, getLocalizedText, formatDate, formatBirthDate } from './resumeUtils';
import { formatTextForDisplay, escapeHtml } from './textFormatter';

interface Template01Props {
  data: ResumeData;
  template: AppliedTemplate;
  accentColor: string;
  accentTextColor: string;
  isAccentDark: boolean;
  bgColor: string;
  bgTextColor: string;
}

export const Template01Renderer: React.FC<Template01Props> = ({
  data,
  template,
  accentColor,
  accentTextColor,
  isAccentDark,
  bgColor,
  bgTextColor,
}) => {
  const t = template;
  const loc = (key: string) => getLocalizedText(key, data.lang, t.translations);

  const decLine = t.colors.decorativeLine || t.colors.secondary || '#ECECEC';
  const textColor = t.colors.text || '#303030';

  const dotStyle: React.CSSProperties = {
    borderBottom: '1.5px dotted #000000',
    minHeight: '1.5px',
    margin: '0 8px',
    height: '1.5px',
    alignSelf: 'flex-end',
    display: 'inline-block',
    verticalAlign: 'bottom',
    lineHeight: '1',
    flexShrink: 0,
    marginBottom: '2px',
  };

  const separatorStyle: React.CSSProperties = {
    fontSize: `${t.fonts.scale.base}px`,
    fontWeight: 400,
    borderBottom: `1px dotted ${decLine}`,
    minHeight: '1px',
    margin: '0 4px',
  };

  const renderSectionTitle01 = (title: string) => (
    <div className="mb-4" style={{ textAlign: 'center', position: 'relative' }}>
      <div className="h-px mb-2" style={{
        backgroundColor: decLine,
        maxWidth: '100px',
        margin: '0 auto 8px',
      }} />
      <h2 className="section-title template-01-header" style={{
        fontSize: '17px',
        fontWeight: 600,
        textTransform: 'uppercase',
        color: '#303030',
        ...(isAccentDark ? {
          WebkitTextStroke: `1.5px ${bgColor || '#FFFFFF'}`,
          paintOrder: 'stroke fill' as const,
        } : {}),
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: '16px',
        paddingRight: '16px',
        marginTop: 0,
        marginBottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        boxSizing: 'border-box' as const,
        textAlign: 'center',
        lineHeight: '1.1',
        position: 'relative',
        zIndex: 2,
        background: 'transparent',
      }}>
        {title}
      </h2>
      <div className="template-01-background-block" style={{
        position: 'relative',
        width: '100%',
        background: accentColor,
        height: '18px',
        maxHeight: '18px',
        minHeight: '18px',
        marginTop: '-0.65rem',
        marginBottom: '8px',
        zIndex: 1,
      }} />
      <div className="h-px" style={{
        backgroundColor: decLine,
        width: '100%',
        marginTop: '8px',
      }} />
    </div>
  );

  const renderHeader = () => (
    <div className="mb-4" style={{ textAlign: 'center' }}>
      <h1 className="mb-1" style={{
        fontSize: `${t.fonts.scale.h1}px`,
        textTransform: 'uppercase',
        fontWeight: 600,
        color: t.colors.text || t.colors.primary || '#303030',
        marginBottom: '2px',
      }}>
        {data.name?.last || ''} {data.name?.first || ''} {data.name?.middle || ''}
      </h1>
      <p className="mb-2" style={{
        fontSize: `${t.fonts.scale.h3}px`,
        fontWeight: 400,
        color: t.colors.text || t.colors.primary || '#303030',
        marginBottom: '8px',
      }}>
        {data.position}
      </p>
      <div className="h-px" style={{
        backgroundColor: decLine,
        maxWidth: '200px',
        margin: '0 auto',
      }}>
        <div className="h-full" style={{
          backgroundColor: t.colors.accent || t.colors.text || t.colors.primary || '#303030',
          width: '100%',
        }} />
      </div>
    </div>
  );

  const renderContacts = () => {
    if (!data.contacts?.email && (!data.contacts?.phones || data.contacts.phones.length === 0)) return null;
    return (
      <div className="space-y-2 mb-3">
        {renderSectionTitle01(loc('contacts'))}
        <div className="section-content" style={{ fontSize: `${t.fonts.scale.base}px` }}>
          <div className="flex justify-between items-center" style={{ fontSize: `${t.fonts.scale.base}px` }}>
            {data.contacts?.phones && data.contacts.phones.length > 0 && (
              <div className="flex items-center flex-1" style={{ paddingRight: '20px' }}>
                <span style={{ color: bgTextColor, whiteSpace: 'nowrap', fontSize: `${t.fonts.scale.base}px` }}>Телефон</span>
                <span className="mx-1 flex-1 template-01-dots" style={dotStyle} />
                <span style={{ color: bgTextColor, whiteSpace: 'nowrap', fontSize: `${t.fonts.scale.base}px` }}>{data.contacts.phones[0]}</span>
              </div>
            )}
            {data.contacts?.email && (
              <div className="flex items-center flex-1" style={{ paddingLeft: '20px' }}>
                <span style={{ color: bgTextColor, whiteSpace: 'nowrap', fontSize: `${t.fonts.scale.base}px` }}>Email</span>
                <span className="mx-1 flex-1 template-01-dots" style={dotStyle} />
                <span style={{ color: bgTextColor, whiteSpace: 'nowrap', fontSize: `${t.fonts.scale.base}px` }}>{data.contacts.email}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!data.about || data.about.trim() === '') return null;
    return (
      <div className="space-y-2">
        {renderSectionTitle01(loc('summary'))}
        <div className="section-content" style={{ fontSize: `${t.fonts.scale.base}px`, textAlign: 'left' }}>
          <div dangerouslySetInnerHTML={{ __html: escapeHtml(data.about).replace(/\\n/g, '\n').replace(/\n/g, '<br>') }} />
        </div>
      </div>
    );
  };

  const renderExperience = () => {
    if (!data.experience || data.experience.length === 0) return null;
    return (
      <div className="space-y-2">
        {renderSectionTitle01(loc('experience'))}
        <div className="space-y-2">
          {data.experience.map((exp, index) => (
            <div key={index} className="avoid-break">
              <div className="flex items-center mb-1" style={{ fontSize: `${t.fonts.scale.h3}px` }}>
                <span className="font-semibold text-gray-900">• {exp.company}</span>
                <span className="text-gray-900 mx-1 flex-1" style={separatorStyle} />
                <span className="text-gray-900" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                  {exp.dateFrom ? formatDate(exp.dateFrom) : '—'} – {exp.dateTo ? formatDate(exp.dateTo) : 'н.в.'}
                </span>
              </div>
              <div className="mb-1">
                <span className="text-xs italic text-gray-900" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                  {exp.title}
                </span>
              </div>
              {exp.description && (
                <div
                  className="section-content mt-2"
                  style={{ fontSize: `${t.fonts.scale.base}px`, color: t.colors.text }}
                  dangerouslySetInnerHTML={{ __html: formatTextForDisplay(exp.description) }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderEducation = () => {
    if (!data.education || data.education.length === 0) return null;
    return (
      <div className="space-y-2">
        {renderSectionTitle01(loc('education'))}
        <div className="space-y-3">
          {data.education.map((edu, index) => (
            <div key={index} className="avoid-break">
              <div className="flex items-center mb-1" style={{ fontSize: `${t.fonts.scale.h3}px` }}>
                <span className="font-semibold text-gray-900">
                  • {edu.org}{edu.degree ? `, ${edu.degree}` : ''}{edu.specialty ? `, ${edu.specialty}` : ''}{edu.faculty ? `, ${edu.faculty}` : ''}
                </span>
                <span className="text-gray-900 mx-1 flex-1" style={separatorStyle} />
                <span className="text-gray-900" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                  {edu.graduationYear}
                </span>
              </div>
              {edu.degree && (
                <div className="mb-1">
                  <span className="text-xs italic text-gray-900" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                    {edu.degree}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCourses = () => {
    if (!data.courses || data.courses.length === 0) return null;
    return (
      <div className="space-y-2">
        {renderSectionTitle01(loc('courses'))}
        <div className="space-y-3">
          {data.courses.map((course, index) => (
            <div key={index} className="avoid-break">
              <div className="flex items-center mb-1" style={{ fontSize: `${t.fonts.scale.h3}px` }}>
                <span className="font-semibold text-gray-900">• {course.organization || 'Название организации'}</span>
                <span className="text-gray-900 mx-1 flex-1" style={separatorStyle} />
                <span className="text-gray-900" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                  {course.year ? course.year : 'Дата'}
                </span>
              </div>
              <div className="mb-1" style={{ fontSize: `${t.fonts.scale.base}px`, color: textColor }}>
                {course.name || 'Название курса'}
              </div>
              {course.description && (
                <div className="section-content mt-1" style={{
                  fontSize: `${t.fonts.scale.base}px`,
                  color: t.colors.text,
                  marginLeft: '12px',
                }}>
                  {course.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSkills = () => {
    if (!data.skills || data.skills.length === 0) return null;
    return (
      <div className="space-y-2">
        {renderSectionTitle01(loc('skills'))}
        <div className="section-content flex flex-wrap gap-x-8 gap-y-2" style={{ fontSize: `${t.fonts.scale.base}px` }}>
          {data.skills.map((skill, index) => (
            <span key={index} style={{ color: textColor }}>{skill}</span>
          ))}
        </div>
      </div>
    );
  };

  const renderLanguages = () => {
    if (!data.languages || data.languages.length === 0) return null;
    return (
      <div className="space-y-2">
        {renderSectionTitle01(loc('languages'))}
        <div className="section-content space-y-3" style={{ fontSize: `${t.fonts.scale.base}px` }}>
          {data.languages.map((lang, index) => (
            <div key={index}>
              <div className="text-gray-900 font-medium" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                {lang.name}
              </div>
              <div className="text-gray-900" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                {lang.level}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderHobbies = () => {
    if (!data.hobbies || data.hobbies.length === 0) return null;
    return (
      <div className="space-y-2">
        {renderSectionTitle01(loc('hobbies'))}
        <div className="space-y-3">
          {data.hobbies.map((hobby, index) => (
            <div key={index} className="avoid-break">
              <div className="mb-1" style={{ fontSize: `${t.fonts.scale.base}px` }}>
                <span style={{ color: textColor }}>• </span>
                <span className="font-semibold" style={{ color: textColor }}>
                  {typeof hobby === 'string' ? hobby : hobby.name}
                </span>
              </div>
              {typeof hobby !== 'string' && hobby.description && (
                <div className="section-content" style={{
                  fontSize: `${t.fonts.scale.base}px`,
                  color: textColor,
                  marginLeft: '12px',
                }}>
                  {hobby.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMeta = () => {
    const items: Array<{ label: string; value: string }> = [];
    if (data.location?.city) items.push({ label: 'Город проживания', value: data.location.city });
    if (data.birthDate) items.push({ label: 'Дата рождения', value: formatBirthDate(data.birthDate) });
    if (data.meta?.citizenship) items.push({ label: 'Гражданство', value: data.meta.citizenship });
    if (data.meta?.employment) items.push({ label: 'Занятость', value: data.meta.employment });
    if (data.meta?.schedule) items.push({ label: 'График работы', value: data.meta.schedule });
    if (data.meta?.relocation) items.push({ label: 'Готов к переезду', value: 'Да' });
    if (data.meta?.travelReady) items.push({ label: 'Готов к командировкам', value: 'Да' });
    if (items.length === 0) return null;

    return (
      <div className="space-y-2">
        {renderSectionTitle01('ДОПОЛНИТЕЛЬНАЯ ИНФОРМАЦИЯ')}
        <div className="space-y-2" style={{ fontSize: `${t.fonts.scale.base}px` }}>
          {items.map((item, i) => (
            <div key={i} className="flex justify-between items-center" style={{ paddingRight: i % 2 === 0 ? '20px' : 0 }}>
              <span style={{ color: textColor }}>{item.label}</span>
              <span className="mx-1 flex-1" style={{ borderBottom: `1px dotted ${decLine}`, minWidth: '50px' }} />
              <span style={{ color: textColor }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .resume-single-column { display: block; height: 100%; }
        .single-content { background: ${bgColor}; color: ${bgTextColor}; padding: 0; }

        .template-template-01 .single-content > div:first-child {
          background: ${t.colors.bg || t.colors.accent || '#FFFFFF'};
        }

        .experience-card, .education-card, .course-card {
          max-width: 100%; overflow: hidden; word-wrap: break-word;
          border: none; box-shadow: none;
        }

        .template-template-01 .section-title.template-01-header {
          text-align: center !important; font-weight: 600 !important;
          text-transform: uppercase !important; font-size: 17px !important;
          margin-top: 0 !important; margin-bottom: 0 !important;
          position: relative !important; z-index: 2 !important;
          display: flex !important; align-items: center !important;
          justify-content: center !important; width: 100% !important;
          box-sizing: border-box !important; padding: 0 16px !important;
          line-height: 1.1 !important; background: transparent !important;
        }
        .template-template-01 h2.section-title.template-01-header {
          margin-top: 0 !important; margin-bottom: 0 !important;
          padding-top: 0 !important; padding-bottom: 0 !important;
          align-items: center !important; display: flex !important;
          z-index: 2 !important; font-size: 17px !important;
        }
        .template-template-01 .template-01-background-block {
          height: 18px !important; max-height: 18px !important;
          min-height: 18px !important; box-sizing: border-box !important;
        }
        .template-template-01 .template-01-dots {
          border-bottom: 1.5px dotted #000000 !important;
          min-height: 1.5px !important; height: 1.5px !important;
          display: inline-block !important; vertical-align: bottom !important;
          margin-bottom: 2px !important; align-self: flex-end !important;
        }

        .template-template-01 .section-content {
          font-size: ${t.fonts.scale.base}px; line-height: 1;
          color: ${t.colors.text || '#111827'};
        }
        .template-template-01 .section-content ul {
          line-height: 1.5; list-style-type: disc; padding-left: 16px; margin: 0;
        }
        .template-template-01 .section-content ul li { margin: 0; padding: 0; }

        .a4-sheet .space-y-4>*+*{margin-top:.5rem} .a4-sheet .space-y-2>*+*{margin-top:.5rem} .a4-sheet .space-y-3>*+*{margin-top:.75rem}
        .a4-sheet .text-3xl{font-size:1.875rem;line-height:2.25rem} .a4-sheet .text-xl{font-size:1.25rem;line-height:1.75rem}
        .a4-sheet .text-lg{font-size:1.125rem;line-height:1.75rem} .a4-sheet .text-sm{font-size:.875rem;line-height:1.25rem} .a4-sheet .text-xs{font-size:.75rem;line-height:1rem}
        .a4-sheet .font-bold{font-weight:700} .a4-sheet .font-semibold{font-weight:600} .a4-sheet .font-medium{font-weight:500}
        .a4-sheet .text-gray-900{color:#111827} .a4-sheet .text-gray-700{color:#374151} .a4-sheet .text-gray-600{color:#4b5563}
        .a4-sheet .border-gray-200{border-color:#e5e7eb} .a4-sheet .border-b{border-bottom-width:1px} .a4-sheet .border-2{border-width:2px}
        .a4-sheet .rounded-full{border-radius:9999px} .a4-sheet .p-4{padding:1rem} .a4-sheet .pb-2{padding-bottom:.5rem}
        .a4-sheet .mb-1{margin-bottom:.25rem} .a4-sheet .mb-2{margin-bottom:.5rem} .a4-sheet .mb-3{margin-bottom:.75rem} .a4-sheet .mb-4{margin-bottom:1rem}
        .a4-sheet .ml-2{margin-left:.5rem} .a4-sheet .mt-1{margin-top:.25rem} .a4-sheet .mt-2{margin-top:.5rem}
        .a4-sheet .flex{display:flex} .a4-sheet .flex-wrap{flex-wrap:wrap} .a4-sheet .justify-center{justify-content:center}
        .a4-sheet .justify-between{justify-content:space-between} .a4-sheet .items-center{align-items:center} .a4-sheet .gap-2{gap:.5rem}
        .a4-sheet .flex-shrink-0{flex-shrink:0} .a4-sheet .flex-1{flex:1 1 0%}
        .a4-sheet .w-3{width:.75rem} .a4-sheet .h-3{height:.75rem} .a4-sheet .w-full{width:100%}
        .a4-sheet .object-cover{object-fit:cover} .a4-sheet .whitespace-nowrap{white-space:nowrap}
        .a4-sheet ul{list-style-type:disc;padding-left:16px;margin:0} .a4-sheet ul li{margin:0;padding:0}
        .a4-sheet .avoid-break{break-inside:avoid;page-break-inside:avoid} .a4-sheet .section-title{break-after:avoid;page-break-after:avoid}

        @media print {
          .a4-sheet .single-content { background: white !important; color: #111 !important; }
          .a4-sheet a { color: #111 !important; text-decoration: none !important; }
        }
      `}} />

      <div className="resume-single-column">
        <div className="single-content" style={{ display: 'block', height: '100%', background: bgColor }}>
          <div style={{ padding: '12mm 15mm', background: bgColor }}>
            {renderHeader()}
            {renderContacts()}
            {renderSummary()}
            {renderExperience()}
            {renderEducation()}
            {renderCourses()}
            {renderSkills()}
            {renderLanguages()}
            {renderHobbies()}
            {renderMeta()}
          </div>
        </div>
      </div>
    </>
  );
};

export default Template01Renderer;
